import { useState } from 'react';
import { ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/utils';

export interface TenantFilterValues {
  search: string;
  status: string;
  subscription_type: string;
  integration_type: string;
}

interface TenantFilterSidebarProps {
  filters: TenantFilterValues;
  onFilterChange: (key: keyof TenantFilterValues, value: string) => void;
  onClearFilters: () => void;
  className?: string;
}

const statusOptions = [
  { value: 'active', label: 'نشط' },
  { value: 'inactive', label: 'غير نشط' },
  { value: 'suspended', label: 'معلق' },
];

const subscriptionOptions = [
  { value: 'basic', label: 'أساسي' },
  { value: 'premium', label: 'مميز' },
  { value: 'enterprise', label: 'مؤسسات' },
];

const integrationOptions = [
  { value: 'api', label: 'API' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'manual', label: 'يدوي' },
];

export function TenantFilterSidebar({
  filters,
  onFilterChange,
  onClearFilters,
  className,
}: TenantFilterSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const hasActiveFilters = Object.values(filters).some((value) => value !== '');

  return (
    <div
      className={cn(
        'relative transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-12' : 'w-52',
        className
      )}
    >
      {isCollapsed ? (
        <div className="flex h-full flex-col items-center pt-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsCollapsed(false)}
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Filter className="h-5 w-5 text-muted-foreground mt-2" />
          {hasActiveFilters && (
            <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
          )}
        </div>
      ) : (
        <div className="space-y-2 pe-2 h-full overflow-y-auto pb-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4" />
              فلترة
            </h3>
            <div className="flex items-center gap-1">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-destructive hover:text-destructive px-2"
                  onClick={onClearFilters}
                >
                  مسح
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsCollapsed(true)}
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute end-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="اسم الشريك أو البريد"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="h-9 text-xs pe-8"
            />
          </div>

          {/* Status Filter */}
          <SearchableSelect
            options={statusOptions}
            value={filters.status}
            onValueChange={(v) => onFilterChange('status', v)}
            placeholder="الحالة"
            searchPlaceholder="بحث..."
          />

          {/* Subscription Type Filter */}
          <SearchableSelect
            options={subscriptionOptions}
            value={filters.subscription_type}
            onValueChange={(v) => onFilterChange('subscription_type', v)}
            placeholder="نوع الاشتراك"
            searchPlaceholder="بحث..."
          />

          {/* Integration Type Filter */}
          <SearchableSelect
            options={integrationOptions}
            value={filters.integration_type}
            onValueChange={(v) => onFilterChange('integration_type', v)}
            placeholder="نوع التكامل"
            searchPlaceholder="بحث..."
          />
        </div>
      )}
    </div>
  );
}
